import Notification from '@rio-cloud/rio-uikit/Notification';
import Button from 'components/actions/Button';
import { useState } from 'react';
import { createSimulatedVehicle } from 'services/simulatedVehicles';

import type { ContainerBackendCreationError } from './createVehicleWithContainerBackend';
import {
    monitorSimulatedVehicleCreation,
    notifySimulatedVehicleCreationStarted,
    type SimulatedVehicleCreation,
    showNotification,
} from './simulatedVehicleCreationNotifications';
import type { SimulationConfig } from './vehicle-dialog/CreateVehicleForm';

const MAX_NORMALIZED_NAME_LENGTH = 255;

const normalizeSimulatedVehicleName = (name: string, fallback: string) =>
    name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') ||
    fallback ||
    'simulated-vehicle';

interface CreateContainerBackendOptions {
    vin: string;
    name: string;
    externalId?: string | null;
    simulationConfig: SimulationConfig;
}

export const createContainerBackend = async ({
    vin,
    name,
    externalId,
    simulationConfig,
}: CreateContainerBackendOptions): Promise<SimulatedVehicleCreation> => {
    const normalizedName = normalizeSimulatedVehicleName(name, externalId ?? '');
    if (normalizedName.length > MAX_NORMALIZED_NAME_LENGTH) {
        throw new Error(`Normalized vehicle name cannot contain more than ${MAX_NORMALIZED_NAME_LENGTH} characters`);
    }
    const response = await createSimulatedVehicle({
        vin,
        name,
        normalizedName,
        environment: import.meta.env.VITE_SIMULATED_VEHICLES_ENVIRONMENT,
        hocImageTag: simulationConfig.hocImageTag,
        hermodImageTag: simulationConfig.hermodImageTag,
    });
    return { name, vin, vehicleId: response.vehicleId, operationId: response.operationId };
};

interface RetryNotificationOptions {
    vin: string;
    vehicleName: string;
    onRetry: () => Promise<boolean>;
}

interface HandleCreationErrorOptions {
    vehicleName: string;
    onCloseDialog: () => void;
    createContainerBackend: (vin: string, simulationConfig: SimulationConfig) => Promise<SimulatedVehicleCreation>;
}

const RetryAntonioButton = ({ onRetry }: Pick<RetryNotificationOptions, 'onRetry'>) => {
    const [isRetrying, setIsRetrying] = useState(false);
    const [hasSucceeded, setHasSucceeded] = useState(false);

    const handleClick = () => {
        setIsRetrying(true);
        void onRetry().then(succeeded => {
            setHasSucceeded(succeeded);
            setIsRetrying(false);
        });
    };

    return (
        <Button type='button' onClick={handleClick} isLoading={isRetrying} disabled={hasSucceeded}>
            Retry Antonio
        </Button>
    );
};

const showRetryNotification = ({ vin, vehicleName, onRetry }: RetryNotificationOptions) => {
    Notification.error(
        <div>
            <p>
                {vehicleName} was created in VHS with VIN {vin}.
            </p>
            <p>Antonio could not be created. Retry Antonio without creating another vehicle.</p>
            <RetryAntonioButton onRetry={onRetry} />
        </div>,
        {
            title: 'Antonio creation failed',
            timeout: 0,
            closeButton: true,
            hideProgressBar: false,
        }
    );
};

export const handleContainerBackendCreationError = (
    error: ContainerBackendCreationError,
    options: HandleCreationErrorOptions
) => {
    options.onCloseDialog();
    const { vin } = error.vehicle;
    const { vehicleName } = options;

    showRetryNotification({
        vin,
        vehicleName,
        onRetry: () =>
            options.createContainerBackend(vin, error.simulationConfig).then(
                creation => {
                    notifySimulatedVehicleCreationStarted(creation);
                    void monitorSimulatedVehicleCreation(creation);
                    return true;
                },
                () => {
                    showNotification(
                        'error',
                        'Antonio creation failed',
                        `Antonio could not be created. The VHS vehicle remains available with VIN ${vin}.`
                    );
                    return false;
                }
            ),
    });
};

export const handleVehicleCreationSuccess = (
    simulatedVehicleCreation: SimulatedVehicleCreation | undefined,
    vehicleName: string
) => {
    if (simulatedVehicleCreation) {
        notifySimulatedVehicleCreationStarted(simulatedVehicleCreation);
        void monitorSimulatedVehicleCreation(simulatedVehicleCreation);
        return;
    }

    Notification.success(<></>, {
        title: `${vehicleName} was created`,
        timeout: 10_000,
        closeButton: true,
        hideProgressBar: false,
    });
};
